import keyboard from 'bloko/common/constants/keyboard';
import Metrics from 'bloko/common/metrics';
import Media from 'bloko/common/media';
import cssClasses from 'bloko/blocks/suggest/cssClasses';

const {
    KEY_CODES: { ESC, ARROW_UP, ARROW_DOWN, ENTER },
} = keyboard;

const escapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '`': '&#x60;',
};

const unescapeMap = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#x27;': "'",
    '&#x60;': '`',
};

const MIN_BOTTOM_PADDING = 10;
const MIN_DROPDOWN_HEIGHT = 105;

const createEscaper = (map) => {
    const escaper = (match) => map[match];
    const source = `(?:${Object.keys(map).join('|')})`;
    const testRegexp = RegExp(source);
    const replaceRegexp = RegExp(source, 'g');

    return function(string) {
        string = string == null ? '' : `${string}`;
        return testRegexp.test(string) ? string.replace(replaceRegexp, escaper) : string;
    };
};

const normalizeText = (text) => text.trim().toUpperCase();
const escape = createEscaper(escapeMap);
const unescape = createEscaper(unescapeMap);
const isObjectsEqual = (a, b) => {
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);

    if (aProps.length !== bProps.length) {
        return false;
    }

    for (let i = 0; i < aProps.length; i++) {
        const propName = aProps[i];

        if (a[propName] !== b[propName]) {
            return false;
        }
    }

    return true;
};

const createKeyboardHandler = (highlight, hide, show, selectHighlighted) => {
    return (isHidden, event, index) => {
        if (isHidden) {
            if (event.keyCode === ARROW_DOWN) {
                event.preventDefault();
                show();
            }
            return;
        }

        switch (event.keyCode) {
            case ESC:
                event.preventDefault();
                hide();
                break;
            case ARROW_UP:
                event.preventDefault();
                highlight(index - 1);
                break;
            case ARROW_DOWN:
                event.preventDefault();
                highlight(index + 1);
                break;
            case ENTER:
                if (index === -1) {
                    break;
                }
                event.preventDefault();
                selectHighlighted();
                break;
        }
    };
};

const getTransitionStyles = (fadeTime) => {
    return {
        stateStyles: {
            entering: { opacity: 0 },
            entered: { opacity: 1 },
        },
        defaultStyle: {
            transition: `opacity ${fadeTime}ms ease-in-out`,
            opacity: 0,
        },
    };
};

const setMetrics = (elem, dropdown, rightPadding = 0) => {
    const metrics = Metrics.getMetrics(elem);
    const { left, top, height, width } = metrics;
    const dropdownElem = Metrics.getElement(dropdown);

    // Для правильного расчёта максимальной высоты дропдауна
    // он не должен влиять на высоту страницы
    dropdownElem.style.maxHeight = '';
    dropdownElem.style.left = '-9999px';
    dropdownElem.style.top = '-9999px';
    const dropdownMetrics = Metrics.getMetrics(dropdownElem);
    const windowScrollHeight = Metrics.getWindowScrollHeight();

    // В тестах высота `window` равна 0.
    const windowHasSomeSpace = windowScrollHeight > 0;
    const availableHeight = windowScrollHeight - top - height - MIN_BOTTOM_PADDING;
    const maxDropdownHeight = Math.max(availableHeight, MIN_DROPDOWN_HEIGHT);

    let css = {
        left,
        top: top + height,
        width: width + rightPadding,
        right: '',
        maxHeight: windowHasSomeSpace && dropdownMetrics.height > maxDropdownHeight ? maxDropdownHeight : '',
    };

    if (Media.getBreakpoint() === Media.breakpoint.XS) {
        css = {
            left: 0,
            top: css.top,
            width: 'auto',
            right: 0,
        };
    }

    Object.keys(css).forEach((key) => {
        const value = css[key];
        const isNumeric = !isNaN(value) && typeof value === 'number';
        dropdownElem.style[key] = isNumeric ? `${value}px` : value;
    });

    return metrics;
};

const updateHighlight = (suggest, index) => {
    const list = [...suggest.querySelectorAll(`.suggest__item`)];
    const highlightedItem = list.find((el) => el.classList.contains(cssClasses.state.highlighted));

    const MIN = -1;
    const MAX = list.length - 1;
    let newIndex;

    if (index < MIN) {
        newIndex = MAX;
    } else if (index > MAX) {
        newIndex = MIN;
    } else {
        newIndex = index;
    }

    if (highlightedItem && (newIndex === -1 || highlightedItem !== list[newIndex])) {
        highlightedItem.classList.remove(cssClasses.state.highlighted);
    }

    if (newIndex === -1) {
        return -1;
    }

    list[newIndex].classList.add(cssClasses.state.highlighted);

    const dropdownMetrics = Metrics.getMetrics(suggest);
    const itemMetrics = Metrics.getMetrics(list[newIndex]);

    if (!Metrics.isRectangleInRectangle(itemMetrics, dropdownMetrics)) {
        const itemOnTop = list[newIndex].offsetTop < suggest.scrollTop;
        const itemOffsetTop = list[newIndex].offsetTop;
        suggest.scrollTop = itemOnTop
            ? itemOffsetTop
            : itemOffsetTop + list[newIndex].offsetHeight - suggest.offsetHeight;
    }

    return newIndex;
};

export {
    escape,
    unescape,
    normalizeText,
    isObjectsEqual,
    createKeyboardHandler,
    getTransitionStyles,
    setMetrics,
    updateHighlight,
};
